import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Edit, Copy, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { getCategoryBadge } from './documentTemplateConstants';

const DocumentTemplatesGrid = ({ loading, filteredTemplates, handleEdit, handleDuplicate, handleDelete }) => {
  const { t } = useTranslation();
  if (loading) {
    return <div className="text-center py-12 text-muted-foreground">{t('common.loading')}</div>;
  }

  if (filteredTemplates.length === 0) {
    return (
      <Card className="border-border shadow-sm">
        <CardContent className="p-12 text-center text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <p>{t('docComp.gridEmpty')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {filteredTemplates.map((template) => (
        <Card key={template.id} className="border-border shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-foreground">{template.name}</h3>
                  {template.is_default && (
                    <Badge variant="outline" className="text-xs bg-muted text-foreground">{t('docComp.standard')}</Badge>
                  )}
                  {!template.is_active && (
                    <Badge variant="outline" className="text-xs bg-red-950/30 text-red-400">{t('docComp.inactive')}</Badge>
                  )}
                </div>
                {getCategoryBadge(template.category, t)}
              </div>
            </div>

            {template.description && (
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{template.description}</p>
            )}

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={() => handleEdit(template)}
                disabled={template.is_default}
              >
                <Edit className="h-3.5 w-3.5 mr-1" />
                {t('docComp.edit')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleDuplicate(template)}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDelete(template)}
                disabled={template.is_default}
                className="text-red-400 hover:text-red-300 hover:bg-red-950/20"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DocumentTemplatesGrid;
